/**
 * DefaultNavbar — Veronica Family Realtor
 * Clean version: href-based links, no nested dropdowns, mobile menu with flags
 */

import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";

import Container from "@mui/material/Container";
import Icon from "@mui/material/Icon";
import Popper from "@mui/material/Popper";
import Grow from "@mui/material/Grow";
import Divider from "@mui/material/Divider";
import MuiLink from "@mui/material/Link";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

import breakpoints from "assets/theme/base/breakpoints";
import logoVeronica from "assets/images/logo-veronica.png";
import smsIcon from "assets/images/sms.png";
import whatsappIcon from "assets/images/whatsapp.png";

// ─── MOBILE MENU ─────────────────────────────────────────────────────────────

function MobileMenu({ routes, open, language, onLanguageChange }) {
  if (!open) return null;
  return (
    <MKBox shadow="lg" borderRadius="xl" px={2} py={1} mt={1} sx={{ backgroundColor: "white" }}>
      {routes.map(({ name, icon, href }) => (
        <MKBox
          key={name}
          component="a"
          href={href}
          display="flex"
          alignItems="center"
          gap={1.5}
          py={1}
          px={1}
          sx={{
            textDecoration: "none",
            color: "text.primary",
            borderRadius: "8px",
            transition: "background-color 0.2s",
            "&:hover": { backgroundColor: "rgba(0,0,0,0.05)" },
          }}
        >
          <MKBox sx={{ color: "text.secondary", display: "flex" }}>{icon}</MKBox>
          <MKTypography variant="button" color="text" fontWeight="regular">
            {name}
          </MKTypography>
        </MKBox>
      ))}
      <Divider sx={{ my: 1, opacity: 0.15 }} />
      <MKBox display="flex" gap={1} px={1} pb={1}>
        {[
          { lang: "en", flag: "🇺🇸", label: "English" },
          { lang: "es", flag: "🇲🇽", label: "Español" },
        ].map(({ lang, flag, label }) => (
          <MKBox
            key={lang}
            onClick={() => onLanguageChange(lang)}
            title={label}
            sx={{
              fontSize: "1.5rem",
              px: 1,
              py: 0.5,
              borderRadius: "8px",
              cursor: "pointer",
              backgroundColor: language === lang ? "rgba(0,0,0,0.08)" : "transparent",
              border: language === lang ? "2px solid rgba(0,0,0,0.15)" : "2px solid transparent",
              transition: "all 0.18s ease",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.06)" },
            }}
          >
            {flag}
          </MKBox>
        ))}
      </MKBox>
    </MKBox>
  );
}

MobileMenu.propTypes = {
  routes: PropTypes.arrayOf(PropTypes.shape).isRequired,
  open: PropTypes.bool.isRequired,
  language: PropTypes.string.isRequired,
  onLanguageChange: PropTypes.func.isRequired,
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

function DefaultNavbar({
  routes,
  transparent,
  light,
  action,
  smsUrl,
  sticky,
  relative,
  center,
  language,
  onLanguageChange,
}) {
  const [dropdown, setDropdown] = useState("");
  const [dropdownEl, setDropdownEl] = useState("");
  const [dropdownName, setDropdownName] = useState("");
  const [arrowRef, setArrowRef] = useState(null);
  const [mobileNavbar, setMobileNavbar] = useState(false);
  const [mobileView, setMobileView] = useState(false);

  const openMobileNavbar = () => setMobileNavbar((prev) => !prev);

  useEffect(() => {
    function handleResize() {
      if (window.innerWidth < breakpoints.values.lg) {
        setMobileView(true);
        setMobileNavbar(false);
      } else {
        setMobileView(false);
        setMobileNavbar(false);
      }
    }
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ── Desktop nav links ──
  const renderNavbarItems = routes.map(({ name, icon, href, collapse }) => (
    <MKBox
      key={name}
      component="a"
      href={href || "#"}
      display="flex"
      alignItems="center"
      gap={0.5}
      px={1}
      py={0.75}
      sx={{
        textDecoration: "none",
        borderRadius: "8px",
        cursor: "pointer",
        whiteSpace: "nowrap",
        transition: "background-color 0.2s",
        "&:hover": { backgroundColor: "rgba(0,0,0,0.06)" },
      }}
      onMouseEnter={({ currentTarget }) => {
        if (collapse) {
          setDropdown(currentTarget);
          setDropdownEl(currentTarget);
          setDropdownName(name);
        }
      }}
      onMouseLeave={() => collapse && setDropdown(null)}
    >
      <MKBox
        sx={{
          display: "flex",
          alignItems: "center",
          color: light ? "white" : "text.secondary",
        }}
      >
        {icon}
      </MKBox>
      <MKTypography
        variant="button"
        fontWeight="regular"
        color={light ? "white" : "text"}
        sx={{ lineHeight: 1 }}
      >
        {name}
      </MKTypography>
    </MKBox>
  ));

  // ── Dropdown items (for routes with collapse) ──
  const renderRoutes = routes.map(({ name, collapse }) => {
    if (!collapse || name !== dropdownName) return null;
    return collapse.map((item) => {
      const linkProps = item.route
        ? { component: Link, to: item.route }
        : {
            component: MuiLink,
            href: item.href,
            target: "_blank",
            rel: "noreferrer",
          };
      return (
        <MKTypography
          key={item.name}
          {...linkProps}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          variant="button"
          textTransform="capitalize"
          minWidth={item.description ? "14rem" : "12rem"}
          color={item.description ? "dark" : "text"}
          fontWeight={item.description ? "bold" : "regular"}
          py={item.description ? 1 : 0.625}
          px={2}
          sx={({ palette: { grey, dark }, borders: { borderRadius } }) => ({
            borderRadius: borderRadius.md,
            cursor: "pointer",
            transition: "all 300ms linear",
            "&:hover": { backgroundColor: grey[200], color: dark.main },
          })}
        >
          {item.name}
        </MKTypography>
      );
    });
  });

  // ── Dropdown menu ──
  const dropdownMenu = (
    <Popper
      anchorEl={dropdown}
      open={Boolean(dropdown)}
      placement="top-start"
      transition
      style={{ zIndex: 10 }}
      modifiers={[{ name: "arrow", enabled: true, options: { element: arrowRef } }]}
      onMouseEnter={() => setDropdown(dropdownEl)}
      onMouseLeave={() => {
        setDropdown(null);
        setDropdownName("");
      }}
    >
      {({ TransitionProps }) => (
        <Grow
          {...TransitionProps}
          sx={{
            transformOrigin: "left top",
            background: ({ palette: { white } }) => white.main,
          }}
        >
          <MKBox borderRadius="lg">
            <MKTypography variant="h1" color="white">
              <Icon ref={setArrowRef} sx={{ mt: -3 }}>
                arrow_drop_up
              </Icon>
            </MKTypography>
            <MKBox shadow="lg" borderRadius="lg" p={2} mt={2}>
              {renderRoutes}
            </MKBox>
          </MKBox>
        </Grow>
      )}
    </Popper>
  );

  // ── Contact icon buttons (WhatsApp + SMS) ──
  const contactButtons = (
    <MKBox display="flex" gap={0.5} ml={{ xs: "auto", lg: 1.5 }} alignItems="center">
      {action && (
        <MKBox
          component="a"
          href={action.route}
          target="_blank"
          rel="noopener noreferrer"
          title="WhatsApp"
          sx={{
            width: { xs: 38, lg: 42 },
            height: { xs: 38, lg: 42 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition: "transform 0.18s ease",
            "&:hover": { transform: "scale(1.12)" },
          }}
        >
          <MKBox
            component="img"
            src={whatsappIcon}
            alt="WhatsApp"
            sx={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        </MKBox>
      )}
      {smsUrl && (
        <MKBox
          component="a"
          href={smsUrl}
          title="SMS"
          sx={{
            width: { xs: 38, lg: 42 },
            height: { xs: 38, lg: 42 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition: "transform 0.18s ease",
            "&:hover": { transform: "scale(1.12)" },
          }}
        >
          <MKBox
            component="img"
            src={smsIcon}
            alt="SMS"
            sx={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        </MKBox>
      )}
    </MKBox>
  );

  return (
    <Container sx={sticky ? { position: "sticky", top: 0, zIndex: 10 } : null}>
      <MKBox
        py={1}
        px={{ xs: 4, sm: transparent ? 2 : 3, lg: transparent ? 0 : 2 }}
        my={relative ? 0 : 2}
        mx={relative ? 0 : 3}
        width={relative ? "100%" : "calc(100% - 48px)"}
        borderRadius="xl"
        shadow={transparent ? "none" : "md"}
        color={light ? "white" : "dark"}
        position={relative ? "relative" : "absolute"}
        left={0}
        zIndex={3}
        sx={({ palette: { transparent: transparentColor, white }, functions: { rgba } }) => ({
          backgroundColor: transparent ? transparentColor.main : rgba(white.main, 0.8),
          backdropFilter: transparent ? "none" : "saturate(200%) blur(30px)",
        })}
      >
        <MKBox display="flex" justifyContent="space-between" alignItems="center">
          {/* Logo */}
          <MKBox
            component={Link}
            to="/"
            lineHeight={1}
            py={transparent ? 1.5 : 0.5}
            pl={relative || transparent ? 0 : { xs: 0, lg: 1 }}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(15, 15, 15, 0.85)",
              borderRadius: "25px",
              px: { xs: 1.2, lg: 1.5 },
              py: { xs: 0.8, lg: 1 },
              boxShadow:
                "0 4px 6px rgba(0,0,0,0.06), 0 8px 24px rgba(0,0,0,0.10), 0 1px 2px rgba(255,255,255,0.8) inset",
              transition: "box-shadow 0.2s ease",
              "&:hover": {
                boxShadow: "0 6px 12px rgba(0,0,0,0.10), 0 12px 32px rgba(0,0,0,0.14)",
              },
            }}
          >
            <MKBox
              component="img"
              src={logoVeronica}
              alt="Veronica Family Realtor"
              sx={{
                height: { xs: "48px", lg: "56px" },
                width: "auto",
                objectFit: "contain",
              }}
            />
          </MKBox>

          {/* Desktop nav links + flags */}
          <MKBox
            color="inherit"
            display={{ xs: "none", lg: "flex" }}
            alignItems="center"
            ml="auto"
            mr={center ? "auto" : 0}
          >
            {renderNavbarItems}
            <Divider
              orientation="vertical"
              flexItem
              sx={{ mx: 1.5, my: "auto", height: 20, opacity: 0.2 }}
            />
            {[
              { lang: "en", flag: "🇺🇸", label: "English" },
              { lang: "es", flag: "🇲🇽", label: "Español" },
            ].map(({ lang, flag, label }) => (
              <MKBox
                key={lang}
                onClick={() => onLanguageChange(lang)}
                title={label}
                sx={{
                  fontSize: "1.35rem",
                  lineHeight: 1,
                  px: 0.8,
                  py: 0.4,
                  borderRadius: "8px",
                  cursor: "pointer",
                  border:
                    language === lang ? "2px solid rgba(0,0,0,0.25)" : "2px solid transparent",
                  backgroundColor: language === lang ? "rgba(0,0,0,0.06)" : "transparent",
                  transition: "all 0.18s ease",
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.08)",
                    transform: "scale(1.1)",
                  },
                }}
              >
                {flag}
              </MKBox>
            ))}
          </MKBox>

          {/* CTA: WhatsApp + SMS */}
          {contactButtons}

          {/* Mobile hamburger */}
          <MKBox
            display={{ xs: "inline-block", lg: "none" }}
            lineHeight={0}
            py={1.5}
            pl={1.5}
            color={transparent ? "white" : "inherit"}
            sx={{ cursor: "pointer" }}
            onClick={openMobileNavbar}
          >
            <Icon fontSize="default">{mobileNavbar ? "close" : "menu"}</Icon>
          </MKBox>
        </MKBox>

        {/* Mobile nav */}
        <MKBox
          bgColor={transparent ? "white" : "transparent"}
          shadow={transparent ? "lg" : "none"}
          borderRadius="xl"
          px={transparent ? 2 : 0}
        >
          {mobileView && (
            <MobileMenu
              routes={routes}
              open={mobileNavbar}
              language={language}
              onLanguageChange={onLanguageChange}
            />
          )}
        </MKBox>
      </MKBox>

      {dropdownMenu}
    </Container>
  );
}

DefaultNavbar.defaultProps = {
  transparent: false,
  light: false,
  action: false,
  smsUrl: null,
  sticky: false,
  relative: false,
  center: false,
  language: "en",
  onLanguageChange: () => {},
};

DefaultNavbar.propTypes = {
  routes: PropTypes.arrayOf(PropTypes.shape).isRequired,
  transparent: PropTypes.bool,
  light: PropTypes.bool,
  action: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      type: PropTypes.oneOf(["external", "internal"]).isRequired,
      route: PropTypes.string.isRequired,
    }),
  ]),
  smsUrl: PropTypes.string,
  sticky: PropTypes.bool,
  relative: PropTypes.bool,
  center: PropTypes.bool,
  language: PropTypes.string,
  onLanguageChange: PropTypes.func,
};

export default DefaultNavbar;
